import { mapState, mapActions, mapGetters } from 'vuex'
import Zepto from 'zepto-webpack'

import PreloadedAssets from '@/configuration/PreloadedAssets'
import paymentMethodsConf from '@/configuration/sources/smartform/paymentMethodsConf.yml'
import { assets } from '@/common/loader/assets'
import { MethodDescriptor } from '@/common/model/PaymentMethod'

import KryptonLoadingAnimation from '@/host/components/controls/KryptonLoadingAnimation'
import SmartFormCardIcons from '@/host/components/smartform/CardIcons'
import SmartFormRadioButton from '@/host/components/smartform/RadioButton'
import SmartButtonSkeleton from '@/host/components/smartbutton/Skeleton'
import { ApplePayMixin } from '@/host/components/mixins/ApplePay'
import { SmartFormClickMixin } from '@/host/components/mixins/SmartFormClick'
import { NestedMethodContainerMixin } from '@/host/components/mixins/NestedMethodContainer'
import NestedMethod from '@/host/components/smartform/nestedMethod/NestedMethod'
import { debounce } from '@/common/util/debounce'
import { resolveBrand } from '@/common/util/brand'
import NestedMethodCountdown from '@/host/components/smartform/nestedMethod/NestedMethodCountdown'

export const SmartButtonMixin = {
  mixins: [ApplePayMixin, SmartFormClickMixin, NestedMethodContainerMixin],
  components: {
    KryptonLoadingAnimation,
    SmartFormCardIcons,
    SmartFormRadioButton,
    SmartButtonSkeleton,
    NestedMethod,
    NestedMethodCountdown
  },
  data() {
    return {
      hover: false,
      icon: null,
      hasNoCards: false,
      isFallback: false,
      methodIconContent: '<span class="kr-placeholder"></span>',
      css: {
        button: {}
      },
      debouncedUpdateIcon: () => Promise.resolve(undefined)
    }
  },
  computed: {
    ...mapGetters([
      'effectiveCardBrandList',
      'isMethodAvailable',
      'isMethodAvailableInDna',
      'isSinglePaymentButton',
      'hasSeveralPaymentMethods',
      'hasPaymentMethods',
      'hasSmartButton',
      'numOfPaymentMethods',
      'hasCardMethodAvailable',
      'getShortMethodKey',
      'getCustomPaymentMethodLabel',
      'getCustomPaymentMethodIcon',
      'isApplePaySimulator',
      'isFallbackSmartButtonVisible',
      'isSelectedMethod',
      'hasValidToken',
      'translate',
      'cardFormInsideButton',
      'getNestedMethodType',
      'isEffectiveNativeLookMethod',
      'isCardBrandAsMethod',
      'getLongMethodKey',
      'isCountdownVisible'
    ]),
    ...mapState([
      'amountLabel',
      'isUnitaryTest',
      'disabledForm',
      'disabledCardForm',
      'allIFramesReady',
      'activeForm'
    ]),
    ...mapGetters({
      paymentMethods: 'effectivePaymentMethodList'
    }),
    ...mapState({
      errorCode: state => state.error.errorCode,
      forceSkeleton: state => state.smartButton.forceSkeleton,
      activeMethod: state => state.smartForm.activeMethod,
      activeMethodMetadata: state => state.smartForm.activeMethodMetadata,
      selectedMethod: ({ smartForm }) => smartForm.selectedMethod,
      buttonConfig: state => state.form.smartButton.button,
      spinnerVisible: state => state.form.smartButton.button.spinnerVisible,
      paymentDone: state => state.smartForm.paymentDone,
      configIcon({ smartForm }) {
        if (!this.paymentMethod) return
        return smartForm.paymentMethods[this.shortMethodKey]?.icon
      },
      nativeLookMethods: state => state.smartButton.nativeLook.paymentMethods
    }),
    styles() {
      const styles = {}
      styles.button = { ...this.css.button }
      if (this.hover) {
        styles.button.border = this.buttonConfig.$hover.border
      }
      return styles
    },
    dynamicClasses() {
      return {
        'kr-smart-button--spbtn': this.isSinglePaymentButton,
        'kr-smart-button--selected': this.isSelected,
        'kr-smart-button--fallback': this.isFallback,
        'kr-hover': this.hover,
        [this.methodClass]: true,
        'kr-disabled':
          (this.isDisabled && !this.isApplePay) ||
          (this.isDisabled &&
            !this.isMethodAvailableInDna(this.cleanPaymentMethod)) ||
          this.disabledForm,
        'kr-loading': this.isLoading,
        'kr-apple-pay': this.isApplePay,
        'kr-apple-pay--simulator': this.isApplePay && this.isApplePaySimulator,
        'kr-smart-button--nested-method': this.isSmartButtonWithNestedMethod,
        [`kr-smart-button--nested-${this.getNestedMethodType(
          this.paymentMethod
        )?.toLowerCase()}`]: this.isSmartButtonWithNestedMethod,
        'kr-smart-button--native-look': this.isEffectiveNativeLookMethod(
          this.paymentMethod
        )
      }
    },
    isSelected() {
      return this.isSelectedMethod(this.paymentMethod)
    },
    shortMethodKey() {
      return this.getShortMethodKey(this.paymentMethod)
    },
    methodDescriptor() {
      return MethodDescriptor.fromString(this.paymentMethod)
    },
    methodClass() {
      return `kr-${this.shortMethodKey.toLowerCase()}`
    },
    defaultMethodLabel() {
      // Specific translation for smartbutton
      const key = `smartbutton_method_${this.shortMethodKey.toLowerCase()}`
      if (this.translate(key) !== key) return this.translate(key)
      // Use the Pay with + method format
      return `${this.translate('smartbutton_label')} ${
        this.singleBrandLabel ||
        paymentMethodsConf.labels[this.shortMethodKey] ||
        this.shortMethodKey
      }`
    },
    title() {
      return this.isDisabled
        ? this.translate('smartbutton_title_disabled')
        : null
    },
    isApplePay() {
      return this.cleanPaymentMethod === 'APPLE_PAY'
    },
    isLoading() {
      return (
        !this.errorCode &&
        this.isPaymentActive &&
        !this.isEffectiveNativeLookMethod(this.paymentMethod)
      )
    },
    showLoadingAnimation() {
      return this.spinnerVisible && this.isLoading
    },
    showRadioButton() {
      return this.isSinglePaymentButton
    },
    isPaymentActive() {
      return (
        this.activeMethod === this.cleanPaymentMethod &&
        this.activeMethodMetadata === this.metadata &&
        !this.isSmartButtonWithNestedMethod
      )
    },
    cleanPaymentMethod() {
      return this.methodDescriptor.name
    },
    metadata() {
      return this.methodDescriptor.metadata ?? null
    },
    isDisabled() {
      return (
        !this.allIFramesReady ||
        !this.isMethodAvailableInDna(this.cleanPaymentMethod) ||
        this.paymentDone ||
        this.disabledForm ||
        (this.cleanPaymentMethod === 'CARDS' && this.disabledCardForm) ||
        (this.paymentMethod === 'CARDS' &&
          this.effectiveCardBrandList.length === 0) ||
        this.hasNoCards
      )
    },
    isSmartButtonWithNestedMethod() {
      return this.isNestedMethod(this.paymentMethod)
    },
    isSkeletonVisible() {
      return (
        (!this.allIFramesReady && this.isVisible) ||
        !this.hasValidToken ||
        this.forceSkeleton
      )
    },
    renderButton() {
      return this.allIFramesReady && this.isVisible
    },
    isButtonRendered() {
      return !this.isSkeletonVisible && this.renderButton
    }
  },
  watch: {
    isVisible: 'parentVisibility',
    async icon() {
      await this.setMethodIconContent()
    },
    async configIcon() {
      await this.setMethodIconContent()
    },
    async shortMethodKey() {
      await this.debouncedUpdateIcon()
    },
    nativeLookMethods: {
      async handler() {
        await this.debouncedUpdateIcon()
      },
      deep: 1
    },
    async isButtonRendered(val) {
      if (val) {
        await this.$nextTick()
        this.baseButtonHeight = this.$el.getBoundingClientRect().height
      }
    }
  },
  created() {
    this.debouncedUpdateIcon = debounce(() => {
      this.updateIcon()
    }, 150)
    this.debouncedUpdateIcon()
  },
  mounted() {
    this.parentVisibility(this.isVisible)
  },
  methods: {
    ...mapActions(['openMethod', 'selectMethod']),
    async triggerMethod(method, $event) {
      // Single payment button with fallback button will always be selected by default
      // The button is only visual, the payment logic is called from the payment button
      if (this.isSinglePaymentButton && this.isFallbackSmartButtonVisible)
        return

      if (this.isSinglePaymentButton) {
        this.selectMethod(method)
        return
      }

      let action
      if (this.isSmartButtonWithNestedMethod) {
        action = 'methodSelected'
      } else {
        action = this.paymentMethod.startsWith('CARDS')
          ? 'openPopin'
          : 'beforePaymentStart'
      }
      const methodKey = this.isCardBrandAsMethod(this.paymentMethod)
        ? this.getLongMethodKey(this.paymentMethod)
        : this.paymentMethod
      if (await this.interruptsExecution(methodKey, action)) return false

      // If we click on the + button, the button is disabled or the payment done, we
      // avoid the method selection
      const $target = Zepto($event.target)
      if (this.isDisabled || this.isPlusLabel($target)) return

      if (!this.isApplePay) this.loading = true

      this.openMethod(this.methodDescriptor)
    },
    isPlusLabel($target) {
      return $target.hasClass('kr-extra-brands')
    },
    parentVisibility(val) {
      if (this.$el?.parentElement)
        this.$el.parentElement.style.display = val ? 'block' : 'none'
    },
    setNumOfBrands(num) {
      this.hasNoCards = num === 0
    },
    getAsset() {
      const config = assets.paymentMethods.regular
      const resolvedMethod = resolveBrand(this.shortMethodKey, false)

      if (this.isEffectiveNativeLookMethod(this.paymentMethod)) {
        return config[`${resolvedMethod}_NATIVE`]
      }

      return config[this.shortMethodKey] || config[resolvedMethod]
    },
    async setMethodIconContent() {
      const placeholder = '<span class="kr-placeholder"></span>'
      if (!this.paymentMethod) return placeholder

      this.methodIconContent =
        (await this.getCustomPaymentMethodIcon(this.cleanPaymentMethod)) ||
        this.icon ||
        placeholder
    },
    async updateIcon() {
      if (!this.isUnitaryTest) {
        const iconDef = this.getAsset()
        this.icon =
          (iconDef && (await iconDef())) || PreloadedAssets.regular.card
      }
      await this.setMethodIconContent()
    }
  }
}
